import { createEntityAdapter, EntityState } from '@reduxjs/toolkit';

import {
  fetchEpisodeCategoriesRequest,
  fetchEpisodeFacetsRequest,
  fetchFieldsOfStudyRequest,
  fetchLibraryCategoriesRequest,
} from '@/requests';
import {
  EpisodeCategory,
  EpisodeFacet,
  LibraryCategory,
  LibrarySubCategory,
} from '@/types';
import { camelCaseKeys } from '@/utils';

import { postgrestApi } from '../postgrest-api';

export type LibraryCategoryEntity = Omit<LibraryCategory, 'subCategories'> & {
  subCategories: EntityState<LibrarySubCategory, string>;
};

const librarySubCategoriesAdapter = createEntityAdapter<
  LibrarySubCategory,
  string
>({
  selectId: subCategory => subCategory.slug,
});

const libraryCategoriesAdapter = createEntityAdapter<
  LibraryCategoryEntity,
  string
>({
  selectId: category => category.slug,
});

const episodeFacetsAdapter = createEntityAdapter<EpisodeFacet, string>({
  selectId: facet => facet.key,
});

const episodeCategoriesAdapter = createEntityAdapter<EpisodeCategory, string>({
  selectId: category => category.episodeId,
});

export const categoriesApi = postgrestApi.injectEndpoints({
  endpoints: builder => ({
    fetchLibraryCategories: builder.query<
      EntityState<LibraryCategoryEntity, string>,
      Parameters<typeof fetchLibraryCategoriesRequest>[0] | void
    >({
      query: fetchLibraryCategoriesRequest,
      transformResponse: (response: unknown) => {
        const camelCased = camelCaseKeys<LibraryCategory[]>(response) ?? [];

        const categories: LibraryCategoryEntity[] = camelCased.map(
          category => ({
            ...category,
            subCategories: librarySubCategoriesAdapter.setAll(
              librarySubCategoriesAdapter.getInitialState(),
              category.subCategories ?? [],
            ),
          }),
        );

        return libraryCategoriesAdapter.setAll(
          libraryCategoriesAdapter.getInitialState(),
          categories,
        );
      },
    }),
    fetchEpisodeFacets: builder.query<EntityState<EpisodeFacet, string>, void>({
      query: fetchEpisodeFacetsRequest,
      transformResponse: (res: any) =>
        episodeFacetsAdapter.setAll(
          episodeFacetsAdapter.getInitialState(),
          camelCaseKeys(res),
        ),
    }),
    fetchEpisodeCategories: builder.query<
      EntityState<EpisodeCategory, string>,
      Parameters<typeof fetchEpisodeCategoriesRequest>[0]
    >({
      query: fetchEpisodeCategoriesRequest,
      transformResponse: res =>
        episodeCategoriesAdapter.setAll(
          episodeCategoriesAdapter.getInitialState(),
          camelCaseKeys(res),
        ),
    }),
    fetchFieldsOfStudy: builder.query<Record<string, string>, void>({
      query: fetchFieldsOfStudyRequest,
      transformResponse: (res: any[]) =>
        res.reduce((acc, cur) => {
          acc[cur.field_of_study_id] = cur.name;
          return acc;
        }, {}),
    }),
  }),
});

export const {
  useFetchLibraryCategoriesQuery,
  useFetchFieldsOfStudyQuery,
  useFetchEpisodeFacetsQuery,
  useFetchEpisodeCategoriesQuery,
} = categoriesApi;
